<template>
    <div :class="['billing-plans', { loading: ! billingPlan }]">
        <base-loader
            v-if="! billingPlan"
        />
        <template v-else>
            <div
                v-for="price in prices"
                :key="price.id"
                class="plan-wrapper"
            >
                <div
                    class="plan"
                    :class="{ active: activePriceId === price.id }"
                    @click="selectPrice(price)"
                >
                    <div class="plan-container flex">
                        <div class="plan-icon">
                            <svg-vue class="check" icon="check" />
                        </div>
                        <div class="plan-credentials">
                            <div class="plan-name">
                                {{ price.nickname }}
                            </div>
                            <div class="plan-price">
                                {{ usdFormatter(price.monthlyAmount) }}/mo
                            </div>
                        </div>
                    </div>
                </div>
                <p v-if="price.percentageSaving" class="saving-info">
                    Save {{ price.percentageSaving }}% when billed {{ getBilledInfo(price.interval) }}!
                </p>
            </div>

            <div
                v-if="activePrice && 'year' === activePrice.interval"
                class="annual-plan-info"
            >
                You’ll be billed in one payment of {{ usdFormatter(activePrice.annualAmount) }} per year.
            </div>
        </template>
    </div>
</template>
<script>
import { calculatePercentageSaving } from '@/utils/price';
import { mapState } from 'vuex';
import discountEndDate from '@/utils/user/discountEndDate';
import hasValidDiscount from '@/utils/user/hasValidDiscount';
import usdFormatter from '@/utils/usdFormatter';

export default {
    props: {
        activePrice: {
            type: Object,
            default: null,
        },
        billingPlan: {
            required: true,
            type: [Object, Boolean],
        },
        upgradePro: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        ...mapState([
            'user',
            'userSubscription',
        ]),

        /**
         * Returns discount end date
         *
         * @returns  {string}  End date
         */
        discountDate() {
            return discountEndDate(this.userSubscription?.discount?.end);
        },

        /**
         * Checks if user has valid discout
         *
         * @returns  {boolean}  If user has valid discount
         */
        isValidDiscount() {
            return this.user.legacy && hasValidDiscount(this.userSubscription?.discount?.end);
        },

        /**
         * Prepares list of prices to display.
         *
         * @returns {object} List of prices.
         */
        prices() {
            return Object.values(this.billingPlan?.prices).map((price) => ({
                ...price,
                percentageSaving: this.billingPlan?.prices?.month
                    ? calculatePercentageSaving(
                        price,
                        this.billingPlan.prices.month
                    ) : 0,
            }));
        },

        /**
         * Returns object of active price.
         *
         * @returns {number|undefined} Active price id.
         */
        activePriceId() {
            return this.activePrice?.id;
        },
    },

    watch: {
        billingPlan: {
            /**
             * Sets first price as active.
             */
            handler() {
                if (! this.upgradePro) {
                    this.$emit('change', this.prices?.[0]);
                }
            },

            immediate: true,
        },

        /**
         * Sets current price as active.
         */
        userSubscription() {
            if (this.upgradePro) {
                this.activePriceId = this.userSubscription?.interval;

                this.$emit('changed', this.activePriceId);
            }
        },
    },

    methods: {
        usdFormatter,

        /**
         * Select active interval.
         *
         * @param {object} price Stripe price object.
         */
        selectPrice(price) {
            this.$emit('change', price);
        },

        /**
         * Gets text for billing cycle information.
         *
         * @param {string} interval Price interval ('month' or 'year').
         * @returns {string|undefined} Price interval information.
         */
        getBilledInfo(interval) {
            return {
                month: 'monthly',
                year: 'annually',
            }[interval];
        },
    },
};
</script>
